import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import { formatTRY, formatDate } from "@/components/finance/financeLabels";
import { Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const APPROVE_ROLES = new Set(["super_admin", "finance_approver"]);
const today = () => new Date().toISOString().slice(0, 10);
const firstOfPrevMonth = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1); return d.toISOString().slice(0, 10); };
const lastOfPrevMonth = () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 10); };

export default function PeriodClosePage() {
    const { user } = useAuth() || {};
    const isApprover = user && APPROVE_ROLES.has(user.role);
    const [closes, setCloses] = useState([]);
    const [form, setForm] = useState({ period_start: firstOfPrevMonth(), period_end: lastOfPrevMonth(), note: "" });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try { setCloses((await api.get("/admin/finance/period-close")).data || []); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!isApprover) return;
        if (!form.period_start || !form.period_end) { toast.error("Dönem aralığı seçin"); return; }
        try {
            await api.post("/admin/finance/period-close", form);
            toast.success("Dönem kapatıldı");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata");
        }
    };

    return (
        <FinanceShell>
            {!isApprover && (
                <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    Dönem kapatma yalnızca <b>super_admin</b> ve <b>finance_approver</b> rolleri tarafından yapılabilir.
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
                    <h3 className="font-bold text-lg mb-3 flex items-center gap-2"><Lock className="h-5 w-5" /> Yeni Dönem Kapanışı</h3>
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Dönem Başlangıç</label>
                            <input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="block w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="period-start" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Dönem Bitiş</label>
                            <input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="block w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" data-testid="period-end" />
                        </div>
                        <textarea rows={2} placeholder="Not (opsiyonel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="block w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                        <button onClick={submit} disabled={!isApprover} className="w-full px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2" data-testid="period-close-btn">
                            <Lock className="h-4 w-4" /> Dönemi Kapat (Geri Alınamaz)
                        </button>
                        <p className="text-[11px] text-slate-500">Dönem kapatıldıktan sonra bu tarih aralığına yeni kayıt eklenemez. P&L snapshotı kayıt altına alınır.</p>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800"><h3 className="font-bold">Kapatılmış Dönemler</h3></div>
                    {loading ? <div className="py-10 text-center text-slate-500">Yükleniyor...</div> : closes.length === 0 ? (
                        <div className="py-12 text-center text-slate-500">Henüz dönem kapatılmamış.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="period-list">
                            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                                <tr><th className="px-3 py-2 text-left">Dönem</th><th className="px-3 py-2 text-right">Net Kâr</th><th className="px-3 py-2 text-left">Kapatan</th></tr>
                            </thead>
                            <tbody>
                                {closes.map((c) => (
                                    <tr key={c.id} className="border-t border-slate-100 dark:border-slate-800/50">
                                        <td className="px-3 py-2"><b>{formatDate(c.period_start)} → {formatDate(c.period_end)}</b><div className="text-[10px] text-slate-400">{c.closed_at?.slice(0, 16).replace("T", " ")}</div></td>
                                        <td className={`px-3 py-2 text-right font-bold ${(c.totals?.net_profit || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{formatTRY(c.totals?.net_profit)}</td>
                                        <td className="px-3 py-2 text-xs">{c.closed_by}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </FinanceShell>
    );
}
